import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/auth/AuthContext'
import { useOrganization } from '@/hooks/useOrganization'
import { organizationKeys } from '@/hooks/queryKeys'
import { sessionEvents } from '@/helpers/sessionEvents'
import { isRestricted } from '@lib/helpers/billing-state'
import { LockScreen } from './LockScreen'

/**
 * Swaps the app for the lock screen when billing has restricted the signed-in
 * person's organization.
 *
 * Two ways to find out, because either can come first. The organization's own
 * billing state, for its admins. And any request the API refuses with
 * `billing_locked` — for a client, who has no organization to ask about, and for
 * anyone whose grace period ran out while the page was open. The second asks the
 * organization again, so the lock screen shows what the server now says.
 *
 * A super admin is never locked out — in support mode they work inside a locked
 * company normally — and a vendor belongs to no company.
 */
export function BillingGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const organization = useOrganization()
  const [refusedByApi, setRefusedByApi] = React.useState(false)

  React.useEffect(
    () =>
      sessionEvents.onBillingLocked(() => {
        setRefusedByApi(true)
        void queryClient.invalidateQueries({ queryKey: organizationKeys.all })
      }),
    [queryClient],
  )

  if (user?.role === 'SUPER_ADMIN' || user?.role === 'VENDOR') return <>{children}</>
  // The organization's answer, once there is one, is the truth: a company that
  // has since paid is let back in even after a refusal was seen.
  const restricted = organization.data ? isRestricted(organization.data.billing.state) : refusedByApi
  if (restricted) return <LockScreen organization={organization.data ?? null} />
  return <>{children}</>
}
